<!-- eslint-disable vue/no-v-html -->
<template>
  <dialog
    v-if="isOpen"
    class="modal modal-open z-[9999]"
  >
    <div
      class="modal-box p-0 bg-white rounded-[20px] sm:rounded-[24px] shadow-2xl border border-[#043f421a] max-w-2xl w-[calc(100%-24px)] mx-3 sm:mx-auto flex flex-col max-h-[85vh] overflow-hidden font-['Raleway',sans-serif]"
    >
      <!-- Header with Subheading & Role Tabs directly below -->
      <div class="px-5 py-4 sm:px-6 sm:py-5 border-b border-[#f0f2f5] bg-[#fafafa]">
        <div class="flex justify-between items-start gap-3">
          <div class="min-w-0">
            <h2 class="text-base sm:text-[18px] font-bold text-[#043f42] flex items-center gap-2">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#01d680"
                stroke-width="2"
                class="shrink-0"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <span>Invitation Templates</span>
            </h2>
            <p class="text-xs text-[#6b7280] mt-0.5 truncate">
              {{ programme.name || 'Programme' }}
            </p>
          </div>
          <button
            type="button"
            class="text-[#9ca3af] hover:text-[#4b5563] p-1.5 rounded-full hover:bg-black/5 transition-colors shrink-0 cursor-pointer"
            @click="close"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Role Tabs directly below subheading -->
        <div class="flex items-center gap-1.5 mt-3 pt-3 border-t border-gray-100">
          <button
            v-for="role in roles"
            :key="role.key"
            type="button"
            class="px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer"
            :class="activeRole === role.key ? 'bg-[#043f42] text-white shadow-xs' : 'bg-white border border-gray-200 text-[#4b5563] hover:bg-gray-50'"
            @click="activeRole = role.key"
          >
            <span>{{ role.icon }}</span>
            <span>{{ role.label }}</span>
          </button>
        </div>
      </div>

      <!-- Modal Body -->
      <div class="p-5 sm:p-6 overflow-y-auto flex-1 font-['Raleway',sans-serif]">
        <!-- Edit / Preview Switcher (pushed below, right-hand side of the modal) -->
        <div class="flex justify-end items-center mb-3">
          <div class="flex bg-[#f3f4f6] p-0.5 rounded-full text-xs font-medium border border-gray-200/60">
            <button
              type="button"
              class="px-3 py-1 rounded-full transition-all cursor-pointer"
              :class="viewMode === 'edit' ? 'bg-white text-[#043f42] shadow-xs font-bold' : 'text-[#6b7280] hover:text-black'"
              @click="viewMode = 'edit'"
            >
              Edit
            </button>
            <button
              type="button"
              class="px-3 py-1 rounded-full transition-all cursor-pointer"
              :class="viewMode === 'preview' ? 'bg-white text-[#043f42] shadow-xs font-bold' : 'text-[#6b7280] hover:text-black'"
              @click="viewMode = 'preview'"
            >
              Preview
            </button>
          </div>
        </div>

        <!-- EDIT MODE -->
        <div
          v-if="viewMode === 'edit'"
          class="flex flex-col gap-4"
        >
          <!-- Subject Line -->
          <div class="flex flex-col gap-1">
            <label class="text-xs font-bold text-[#043f42]">Subject</label>
            <input
              ref="subjectInputRef"
              v-model="templates[activeRole].subject"
              type="text"
              class="w-full px-3.5 py-2 bg-white border border-[#d1d5db] rounded-[10px] text-xs sm:text-[13px] text-[#111827] focus:outline-none focus:border-[#01d680] focus:ring-1 focus:ring-[#01d680]"
              placeholder="Email subject..."
            >
          </div>

          <!-- Email Body Textarea -->
          <div class="flex flex-col gap-1">
            <div class="flex justify-between items-center">
              <label class="text-xs font-bold text-[#043f42]">Body</label>
              <button
                type="button"
                class="text-xs text-[#01d680] hover:underline font-semibold cursor-pointer"
                @click="resetCurrentRoleDefault"
              >
                Reset default
              </button>
            </div>
            <textarea
              ref="bodyTextareaRef"
              v-model="templates[activeRole].body"
              rows="8"
              class="w-full px-3.5 py-2.5 bg-white border border-[#d1d5db] rounded-[10px] text-xs sm:text-[13px] text-[#111827] font-mono focus:outline-none focus:border-[#01d680] focus:ring-1 focus:ring-[#01d680] leading-relaxed resize-y"
              placeholder="Email body..."
            />
          </div>
        </div>

        <!-- PREVIEW MODE -->
        <div
          v-else
          class="flex flex-col gap-3"
        >
          <div class="bg-[#f9fafb] border border-[#e5e7eb] rounded-[14px] p-4">
            <div class="border-b border-[#e5e7eb] pb-2.5 mb-3">
              <div class="text-xs text-[#6b7280]">
                <span class="font-semibold text-[#374151]">To:</span> {{ sampleData.name }} &lt;{{ sampleData.email }}&gt;
              </div>
              <div class="text-xs text-[#6b7280] mt-0.5">
                <span class="font-semibold text-[#374151]">From:</span> Doccu &lt;noreply@doccu.co.za&gt;
              </div>
              <div class="text-xs sm:text-sm font-bold text-[#111827] mt-2">
                {{ previewSubject }}
              </div>
            </div>

            <!-- Email Body Rendered Box (Branded Doccu Card) -->
            <div class="bg-white border border-[#e2e8f0] rounded-[16px] overflow-hidden shadow-sm max-w-md mx-auto">
              <!-- Top Accent Bar -->
              <div class="h-1 bg-[#043f42] w-full" />

              <div class="p-5 sm:p-6 text-left">
                <!-- Brand Logo Header -->
                <div class="flex justify-center mb-4">
                  <img
                    src="/images/doccu_logo_email.png"
                    alt="Doccu"
                    class="h-7 w-auto"
                  >
                </div>

                <!-- Headline -->
                <h3 class="text-sm sm:text-base font-extrabold text-[#043f42] text-center mb-1">
                  {{ activeRole === 'sponsor' ? 'Host Employer: Signature Required' : 'Learnership Agreement: Signature Required' }}
                </h3>
                <p class="text-[11px] text-[#64748b] text-center font-medium mb-4">
                  {{ activeRole === 'sponsor' ? 'Batch Tripartite Learnership Agreements' : 'Action Required: Please Sign Document' }}
                </p>

                <div class="text-xs sm:text-sm text-[#374151] leading-relaxed whitespace-pre-line">
                  <span>{{ previewParts.beforeButton }}</span>
                  <div
                    v-if="previewParts.hasButton"
                    class="my-4 text-center"
                  >
                    <span
                      class="inline-block bg-[#01d680] text-[#043f42] font-bold px-6 py-2.5 rounded-full text-xs sm:text-sm select-none shadow-xs transition-transform hover:scale-105 cursor-pointer tooltip tooltip-bottom"
                      :data-tip="activeRole === 'sponsor' ? 'Opens the Bulk Signing Portal for all batch documents' : 'Opens the Learner Signing Portal'"
                    >
                      {{ activeRole === 'sponsor' ? 'Review & Sign Batch Agreements' : 'Review and Sign Agreement' }}
                    </span>
                  </div>
                  <span v-if="previewParts.hasButton">{{ previewParts.afterButton }}</span>
                </div>

                <!-- Email Legal Footer -->
                <div class="mt-6 pt-4 border-t border-[#f1f5f9] text-center">
                  <p class="text-[10px] text-[#94a3b8] leading-normal m-0">
                    Protected by Doccu in compliance with the South African ECT Act 25 of 2002. Every action is recorded in an immutable audit trail.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="px-5 py-3.5 sm:px-6 sm:py-4 border-t border-[#f0f2f5] bg-[#fafafa] flex justify-between items-center">
        <button
          type="button"
          class="px-3.5 py-1.5 text-xs font-semibold text-[#6b7280] hover:text-[#111827] rounded-[10px] transition-colors cursor-pointer"
          @click="close"
        >
          Cancel
        </button>

        <button
          type="button"
          :disabled="isSaving"
          class="px-5 py-2 bg-[#01d680] hover:bg-[#00c070] text-[#043f42] font-bold text-xs sm:text-sm rounded-full transition-colors flex items-center gap-1.5 disabled:opacity-50 cursor-pointer shadow-xs"
          @click="saveTemplates"
        >
          <svg
            v-if="isSaving"
            class="animate-spin h-3.5 w-3.5 text-[#043f42]"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span>{{ isSaving ? 'Saving...' : 'Save' }}</span>
        </button>
      </div>
    </div>
    <form
      method="dialog"
      class="modal-backdrop bg-[#00000099]"
    >
      <button
        type="button"
        @click.prevent="close"
      >
        close
      </button>
    </form>
  </dialog>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  programme: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['close', 'saved'])

const activeRole = ref('student')
const viewMode = ref('edit')
const isSaving = ref(false)
const subjectInputRef = ref(null)
const bodyTextareaRef = ref(null)

const roles = [
  { key: 'student', label: 'Learner', icon: '🎓' },
  { key: 'sponsor', label: 'Sponsor', icon: '🏢' }
]

const getDefaultTemplates = () => ({
  student: {
    subject: 'Action Required: Please sign the {{programme.name}} agreement',
    body: `Hi {{submitter.name}},

You have been invited to review and electronically sign your {{programme.name}} learnership agreement for {{cohort.name}}.

{{submitter.link}}

If you have any questions, please contact your training provider at {{sender.email}}.

Thanks,
{{account.name}}`
  },
  sponsor: {
    subject: 'Action Required: Host Employer signature for {{programme.name}} ({{cohort.name}})',
    body: `Hi {{sponsor.company}},

You have been requested by {{account.name}} to review and electronically sign the tripartite SETA learnership agreements for {{programme.name}} ({{cohort.name}}).

All learner agreements have been signed by the learners and training provider, and are now ready for your host employer authorization.

{{submitter.link}}

If you have any questions, please contact your training provider at {{sender.email}}.

Thanks,
{{account.name}}`
  }
})

const templates = reactive(getDefaultTemplates())

// Initialize from programme template preferences if available
watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    try {
      let prefs = props.programme?.template?.preferences
      if (typeof prefs === 'string') {
        prefs = JSON.parse(prefs)
      }
      const saved = prefs?.email_templates
      if (saved) {
        roles.forEach(r => {
          if (saved[r.key]) {
            templates[r.key].subject = saved[r.key].subject || templates[r.key].subject
            templates[r.key].body = saved[r.key].body || templates[r.key].body
          }
        })
      }
    } catch (e) {
      console.warn('Could not parse template preferences', e)
    }
  }
}, { immediate: true })

const resetCurrentRoleDefault = () => {
  const defaults = getDefaultTemplates()
  templates[activeRole.value].subject = defaults[activeRole.value].subject
  templates[activeRole.value].body = defaults[activeRole.value].body
}

const sampleData = computed(() => {
  const progName = props.programme?.name || 'Further Education and Training Certificate: IT: Systems Development'
  const cohortName = props.programme?.cohort?.name || 'Cohort 1'
  const sponsorName = props.programme?.sponsor_company_name || props.programme?.sponsor_name || 'First National Bank (Pty) Ltd'
  const senderEmail = props.programme?.cohort?.author?.email || 'saonev@protonmail.com'

  if (activeRole.value === 'student') {
    return { name: 'Naledi Mkhize', email: 'naledi.mkhize@example.co.za', progName, cohortName, sponsorName, senderEmail }
  } else {
    return { name: sponsorName, email: props.programme?.sponsor_email || 'sponsor@fnb.co.za', progName, cohortName, sponsorName, senderEmail }
  }
})

const previewSubject = computed(() => {
  let s = templates[activeRole.value].subject || ''
  s = s.replace(/\{\{(?:programme|program)\.name\}\}/gi, sampleData.value.progName)
  s = s.replace(/\{\{cohort\.name\}\}/gi, sampleData.value.cohortName)
  s = s.replace(/\{\{sponsor\.(?:company|company_name)\}\}/gi, sampleData.value.sponsorName)
  s = s.replace(/\{\{submitter\.name\}\}/gi, sampleData.value.name)
  s = s.replace(/\{\{sender\.email\}\}/gi, sampleData.value.senderEmail)
  s = s.replace(/\{\{account\.name\}\}/gi, 'Doccu Skills Institute')
  return s
})

const previewParts = computed(() => {
  let b = templates[activeRole.value].body || ''
  b = b.replace(/\{\{(?:programme|program)\.name\}\}/gi, sampleData.value.progName)
  b = b.replace(/\{\{cohort\.name\}\}/gi, sampleData.value.cohortName)
  b = b.replace(/\{\{sponsor\.(?:company|company_name)\}\}/gi, sampleData.value.sponsorName)
  b = b.replace(/\{\{submitter\.name\}\}/gi, sampleData.value.name)
  b = b.replace(/\{\{sender\.email\}\}/gi, sampleData.value.senderEmail)
  b = b.replace(/\{\{account\.name\}\}/gi, 'Doccu Skills Institute')

  const segments = b.split(/\{\{submitter\.link\}\}/gi)
  return {
    beforeButton: segments[0] || '',
    hasButton: segments.length > 1,
    afterButton: segments.slice(1).join('')
  }
})

const close = () => {
  emit('close')
}

const saveTemplates = async () => {
  isSaving.value = true
  try {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content')
    const cohortId = props.programme.cohort_id || props.programme.cohort?.id
    const progId = props.programme.slug || props.programme.id

    const response = await fetch(`/cohorts/${cohortId}/programmes/${progId}/update_email_template`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        Accept: 'application/json'
      },
      body: JSON.stringify({
        email_templates: templates
      })
    })

    if (!response.ok) throw new Error('Failed to save email templates')
    const data = await response.json()
    emit('saved', data)
    close()
  } catch (error) {
    console.error(error)
    alert('Failed to save email templates: ' + error.message)
  } finally {
    isSaving.value = false
  }
}
</script>
